import { useEffect, useRef, useState, type FormEvent } from 'react';
import { EMOTES, MSG } from '@bb/shared';
import { social } from '../net/api';

const EMOTE_LABEL: Record<string, string> = { wave: '👋 Wave', heart: '❤️ Heart', laugh: '😄 Laugh', dance: '💃 Dance', star: '⭐ Star' };

export interface ChatLine {
  name: string;
  text: string;
  mine: boolean;
  at: number;
}

export function ChatBar({ send, lines }: { send: ((type: string, payload: unknown) => void) | null; lines: ChatLine[] }) {
  const [phrases, setPhrases] = useState<{ id: number; category: string; text: string }[]>([]);
  const [freeChat, setFreeChat] = useState(false);
  const [menu, setMenu] = useState<'quick' | 'emote' | null>(null);
  const [text, setText] = useState('');
  const [showLog, setShowLog] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    social.quick().then((r) => {
      setPhrases(r.phrases);
      setFreeChat(r.freeChatEnabled);
    }, () => {});
  }, []);
  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight });
  }, [lines, showLog]);

  const categories = [...new Set(phrases.map((p) => p.category))];

  function submit(e: FormEvent) {
    e.preventDefault();
    const t = text.trim();
    if (!t || !send) return;
    send(MSG.chat, { text: t });
    setText('');
  }

  return (
    <div className="chatbar">
      {showLog && (
        <div className="chatlog" ref={logRef} aria-live="polite">
          {lines.length === 0 && <p className="muted small-print">No messages yet.</p>}
          {lines.map((l, i) => (
            <p key={i} className={l.mine ? 'mine' : ''}><strong>{l.name}:</strong> {l.text}</p>
          ))}
        </div>
      )}
      {menu === 'quick' && (
        <div className="chat-menu" role="menu">
          {categories.map((c) => (
            <div key={c} className="chat-cat">
              <span className="muted small-print">{c}</span>
              <div className="chip-row">
                {phrases.filter((p) => p.category === c).map((p) => (
                  <button key={p.id} className="chip" role="menuitem" onClick={() => { send?.(MSG.quick, { phraseId: p.id }); setMenu(null); }}>{p.text}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {menu === 'emote' && (
        <div className="chat-menu chip-row" role="menu">
          {EMOTES.map((e) => (
            <button key={e} className="chip" role="menuitem" onClick={() => { send?.(MSG.emote, { emote: e }); setMenu(null); }}>{EMOTE_LABEL[e]}</button>
          ))}
        </div>
      )}
      <form className="chat-row" onSubmit={submit}>
        <button type="button" className={menu === 'quick' ? 'btn small' : 'btn small ghost'} onClick={() => setMenu(menu === 'quick' ? null : 'quick')} disabled={!send}>Say…</button>
        <button type="button" className={menu === 'emote' ? 'btn small' : 'btn small ghost'} onClick={() => setMenu(menu === 'emote' ? null : 'emote')} disabled={!send}>Emote</button>
        {freeChat ? (
          <input id="chat-input" className="chat-input" value={text} maxLength={120} onChange={(e) => setText(e.target.value)} placeholder="Type a message (be kind!)" aria-label="Chat message" autoComplete="off" />
        ) : (
          <span className="muted small-print chat-input">Quick chat only right now.</span>
        )}
        {freeChat && <button className="btn small" disabled={!send || !text.trim()}>Send</button>}
        <button type="button" className="btn small ghost" onClick={() => setShowLog(!showLog)} aria-pressed={showLog}>Log</button>
      </form>
    </div>
  );
}
